import React, { useContext } from 'react'
import classNames from 'classnames'
import { ColumnsProps } from './Columns.d'
import { PageContext } from '@toasttab/shared/src/hooks/usePageContext'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import ImageResolutions from '@toasttab/shared/src/components/atoms/Image/ImageResolutions'
import OneColumnContent from './OneColumnContent'
import TwoColumnContent from './TwoColumnContent'
import { createCssString } from '@toasttab/shared/src/utils/cssUtils'
import Eyebrow from '@toasttab/shared/src/components/molecules/Eyebrow/Eyebrow'

const ColumnTypes: any = {
  oneColumnContent: OneColumnContent,
  twoColumnContent: TwoColumnContent
}

const Columns = ({
  bgColor,
  children = [],
  cssClasses,
  gridClasses,
  icon,
  scrollTrackingSectionName,
  sectionDescription,
  sectionHeading,
  sectionDescriptionClasses,
  eyebrow,
  textAlignment,
  toggleSwitch = false,
  uniqueId,
  dataTestId
}: ColumnsProps) => {
  const pageContext = useContext(PageContext)
  const columnsWrapperClasses = classNames(
    'ds-wrapper',
    createCssString(cssClasses),
    {
      'py-10 md:py-20':
        (!cssClasses || cssClasses[0]?.className === '') &&
        pageContext?.styleContext !== 'request-demo',
      'pb-6 lg:pb-10': pageContext?.styleContext === 'request-demo',
      [bgColor]: !toggleSwitch
    }
  )
  const gridCssClasses = classNames('ds-grid', {
    'items-center justify-items-stretch':
      pageContext?.styleContext !== 'request-demo',
    'justify-items-center text-center lg:justify-items-start lg:text-left':
      pageContext?.styleContext === 'request-demo',
    [gridClasses]: pageContext?.styleContext !== 'request-demo'
  })
  const textColor = classNames({
    'text-white': bgColor === 'bg-brand-50' || bgColor === 'bg-blue-50',
    'text-default': bgColor !== 'bg-brand-50' && bgColor !== 'bg-blue-50'
  })
  const descriptionClasses = classNames(sectionDescriptionClasses, {
    'text-white': bgColor === 'bg-brand-50' || bgColor === 'bg-blue-50',
    'text-default': bgColor !== 'bg-brand-50' && bgColor !== 'bg-blue-50'
  })

  const renderIcon = () => {
    if (icon?.length) {
      return <ImageResolutions images={icon[0]} classes='mb-2' />
    }
  }

  const renderColumns = () => {
    return children.map((column: any, index: number) => {
      const ColumnType = ColumnTypes[column.typeHandle]
      return (
        <ColumnType
          columnContents={column.columnContents || column.children}
          key={index}
          textColor={textColor}
          textAlignment={
            column.typeHandle === 'oneColumnContent' ? textAlignment : 'left'
          }
          type={column.typeHandle}
        />
      )
    })
  }

  return (
    <section
      className={`${toggleSwitch ? bgColor : ''}`}
      id={uniqueId}
      data-tracking-section={scrollTrackingSectionName}
      data-testid={dataTestId}
    >
      <div className={columnsWrapperClasses}>
        <div className='ds-container'>
          {(icon?.length > 0 ||
            sectionHeading ||
            sectionDescription ||
            eyebrow) && (
            <div
              className={`flex flex-col text-${textAlignment} ${
                pageContext?.styleContext === 'request-demo' &&
                'text-center lg:text-left'
              } ${
                pageContext?.styleContext === 'request-demo'
                  ? 'mb-6'
                  : 'mb-8 md:mb-14'
              }`}
            >
              {renderIcon()}
              {eyebrow && (
                <Eyebrow
                  componentClass={`mb-2 ${
                    textAlignment === 'center' ? 'justify-center' : ''
                  } type-overline text-secondary !font-normal`}
                  variation='ds'
                >
                  {eyebrow}
                </Eyebrow>
              )}
              {sectionHeading && (
                <Typography
                  allowHtml
                  as='h2'
                  variation='h2'
                  classes={`${textColor} mb-2`}
                >
                  {sectionHeading}
                </Typography>
              )}
              {sectionDescription && (
                <Typography
                  allowHtml
                  as='p'
                  variation='default'
                  classes={descriptionClasses}
                >
                  {sectionDescription}
                </Typography>
              )}
            </div>
          )}
          <div className={gridCssClasses}>{renderColumns()}</div>
        </div>
      </div>
    </section>
  )
}

export default Columns
